import { useReferentialCallback } from './useReferentialCallback';

type CleanupRoutine = () => void;

/**
 * Represents the function returned by the {@link useEventCleanup} hook.
 *
 * When this function is called, it should be provided with a separate function that should be
 * executed in conjunction with the cleanup routine, {@link CleanupRoutine}, when this function is
 * called.
 */
export type WithEventCleanup = <A extends unknown[], R>(fn: (...args: A) => R) => (...args: A) => R;

/**
 * A hook that returns a referential function, {@link WithEventCleanup}, that wraps the function it
 * is provided with such that a cleanup routine, {@link CleanupRoutine}, is performed before or
 * after the original function executes (depending on the parameters,
 * {@link UseEventCleanupParams}), provided to the method.
 *
 * @example
 * ```tsx
 * const [menuIsOpen, setMenuIsOpen] = useState(false);
 *
 * const close = useCallback(() => setMenuIsOpen(false), [])
 * const withClose = useEventCleanup(close)
 *
 * return (
 *   <Button onClick={withClose(props.onMenuButtonClick)}>
 *     Click Me and Close Menu
 *   </Button>
 * )
 * ```
 */
export const useEventCleanup = (cleanup: CleanupRoutine): WithEventCleanup =>
  useReferentialCallback(<A extends unknown[], R>(fn: (...args: A) => R) => (...args: A): R => {
    cleanup();
    return fn(...args);
  });
